import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { Formik } from 'formik';
import * as Yup from 'yup';
import styled from 'styled-components/macro';
import { isEmpty, find, uniqBy } from 'lodash';
import { useLocation } from 'react-router-dom';

import { getSchedulerByLink } from 'api/scheduler';
import { getClinicSlots } from 'api/clinic';
import Location from 'components/pages/AppointmentScheduler/Location';
import Service from 'components/pages/AppointmentScheduler/Service';
import Scheduler from 'components/pages/AppointmentScheduler/Scheduler';
import Confirm from 'components/pages/AppointmentScheduler/Confirm';
import Loading from 'components/general/Loading';
import { createAppointment } from 'api/appointment';
import { revertPhoneMask } from 'helpers/revert-phone-mask';
import { getServices } from 'api/service';
import Confirmation from './Confirmation';

const Page = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const AppointmentScheduler = () => {
  const { pathname } = useLocation();

  const [date, setDate] = useState(new Date());
  const [selectedSlot, setSelectedSlot] = useState();
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlotIndex, setSelectedSlotIndex] = useState();
  const [selectLocation, setSelectLocation] = useState();
  const [selectService, setSelectService] = useState();
  const [step, setStep] = useState('location');
  const [clinic, setClinic] = useState({});

  const [scheduler, setScheduler] = useState({});
  const [subLocation, setSubLocation] = useState({});
  const [service, setService] = useState({});
  const [serviceOptions, setServiceOptions] = useState();

  const handleDateChange = (newValue) => {
    setDate(newValue);
  };

  const handleSave = async (values) => {
    const { firstName, lastName, email, phone, customFieldValue, dateOfBirth } =
      values;

    try {
      await createAppointment(
        {
          first_name: firstName,
          last_name: lastName,
          email,
          phone: revertPhoneMask(phone),
          custom_field_value: customFieldValue,
          ...(dateOfBirth
            ? { date_of_birth: moment(dateOfBirth).format('YYYY/MM/DD') }
            : {}),
        },
        {
          ...selectedSlot,
          clinic_id: selectedSlot.clinic_id,
        },
      );
      setStep('complete');
    } catch (error) {
      setStep('error');
    }
  };

  useEffect(async () => {
    if (isEmpty(scheduler)) {
      const { data } = await getSchedulerByLink(pathname);

      setScheduler(data);
    }
  }, []);

  const { subLocations, services, organization, schedule } = scheduler;
  const { show_birth_date: showBirthDate, custom_field: customField } =
    schedule || {};

  useEffect(async () => {
    if (selectLocation) {
      const { data } = await getServices(0, 100, selectLocation, schedule.id);

      setServiceOptions(data.data);
    }
  }, [selectLocation]);

  useEffect(async () => {
    if (selectService) {
      setService(find(services, ['id', selectService]));
    }
  }, [selectService]);

  if (isEmpty(scheduler)) {
    return <Loading />;
  }

  if (isEmpty(scheduler)) {
    return null;
  }

  return (
    <Page>
      <Formik
        initialValues={{
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          dateOfBirth: new Date(),
          agree: false,
          customFieldValue: '',
        }}
        validationSchema={Yup.object().shape({
          firstName: Yup.string().required(''),
          lastName: Yup.string().required(''),
          email: Yup.string().email().required(''),
          phone: Yup.string(),
          dateOfBirth: Yup.string().required('').nullable(),
          agree: Yup.boolean().oneOf(
            [true],
            'your agreement to the terms and conditions is required',
          ),
          ...(customField
            ? { customFieldValue: Yup.string().required('') }
            : {}),
        })}
        enableReinitialize
        onSubmit={handleSave}
      >
        {({
          values: {
            firstName,
            lastName,
            email,
            phone,
            dateOfBirth,
            agree,
            customFieldValue,
          },
          errors,
          handleChange,
          submitForm,
          touched,
          setFieldValue,
        }) => (
          <>
            {step === 'location' && (
              <Location
                subLocations={subLocations}
                organization={organization}
                selectLocation={selectLocation}
                setSelectLocation={setSelectLocation}
                setStep={setStep}
              />
            )}

            {step === 'service' && (
              <Service
                organization={organization}
                selectService={selectService}
                setSelectService={setSelectService}
                setStep={setStep}
                serviceOptions={serviceOptions}
              />
            )}

            {step === 'scheduler' && (
              <Scheduler
                organization={organization}
                date={date}
                handleDateChange={handleDateChange}
                selectedSlotIndex={selectedSlotIndex}
                setSelectedSlotIndex={setSelectedSlotIndex}
                selectLocation={selectLocation}
                selectService={selectService}
                schedulerId={scheduler.schedule.id}
                setSelectedSlot={setSelectedSlot}
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                setStep={setStep}
                clinic={clinic}
                setClinic={setClinic}
                subLocation={subLocation}
                setSubLocation={setSubLocation}
                service={service}
                subLocations={subLocations}
              />
            )}

            {step === 'confirm' && (
              <Confirm
                organization={organization}
                selectedSlot={selectedSlot}
                selectedDate={selectedDate}
                setStep={setStep}
                firstName={firstName}
                lastName={lastName}
                email={email}
                phone={phone}
                dateOfBirth={dateOfBirth}
                touched={touched}
                errors={errors}
                agree={agree}
                handleChange={handleChange}
                showBirthDate={showBirthDate}
                submitForm={submitForm}
                setFieldValue={setFieldValue}
                subLocation={subLocation}
                selectService={selectService}
                schedule={schedule}
                customFieldValue={customFieldValue}
              />
            )}

            {step === 'complete' && (
              <Confirmation
                heading="Thank you for using Global Health Solutions"
                description="Your appointment has been successfully booked!"
              />
            )}

            {step === 'error' && (
              <Confirmation
                heading="We're Sorry"
                isError
                description="An error occurred during your submission. Please try again later."
              />
            )}
          </>
        )}
      </Formik>
    </Page>
  );
};

export default AppointmentScheduler;
